/**
 * La scheda dell'evento: dati, partenze e fermate si compilano insieme e
 * si salvano con un pulsante.
 *
 * Prima questa pagina salvava in due modi diversi nello stesso schermo:
 * i dati dell'evento col pulsante, le partenze e le fermate da sole
 * appena si usciva da una casella. Premere "Aggiungi" scriveva subito
 * nel database una partenza senza nome a zero euro, e chi cambiava il
 * titolo e poi usciva dalla pagina lo perdeva senza un avviso.
 *
 * Qui tutto resta in memoria fino al salvataggio, che manda una sola
 * richiesta: o passa tutto, o non cambia niente.
 */
import React, { useState, useEffect, useCallback, useMemo, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { chiediPrimaDiUscire, smettiDiChiedere } from '../../utils/uscita';
import { STATI_EVENTO, euro, ora } from './Eventi';
import {
  ArrowLeft, Plus, Trash2, Save, Bus, MapPin, Users, Ticket, ChevronDown,
  ChevronUp, AlertTriangle, Download, Eye, Clock, Lock, ImagePlus,
  ExternalLink, Printer,
} from 'lucide-react';

/* Chiave locale delle righe non ancora salvate: React ha bisogno di
   distinguerle fra loro prima che il database dia loro un id. */
let contatore = 0;
const chiave = () => `k${++contatore}`;

const ABBANDONA = 'Hai modifiche non salvate su questo evento. Vuoi uscire e perderle?';

const EVENTO_VUOTO = () => ({
  titolo: '', sottotitolo: '', data: '', ora: '', luogo: '',
  indirizzo_arrivo: '', descrizione: '', info_utili: '',
  biglietti_nominativi: false, vendita_apre_il: '', vendita_chiude_il: '',
  note_interne: '', navette: [NAVETTA_VUOTA()],
});

function NAVETTA_VUOTA() {
  return {
    _k: chiave(), nome: '', mezzo: '', posti_totali: '', prezzo: '',
    ora_arrivo: '', ora_rientro: '', attiva: true, venduti: 0, impegnati: 0,
    fermate: [{ _k: chiave(), luogo: '', indirizzo: '', ora: '' }],
  };
}

/**
 * Fra l'ISO dell'API e la casella `datetime-local`, che lavora in ora
 * locale.
 *
 * Tagliare la stringa ISO ai primi 16 caratteri sembrava bastare, ma
 * il server risponde in UTC: una finestra di vendita salvata alle
 * 10:00 si riapriva alle 09:00, e alle 08:00 d'estate. Ogni passaggio
 * la spostava di un'ora.
 */
const perInput = (iso) => {
  if (!iso) return '';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return '';
  const due = (n) => String(n).padStart(2, '0');
  return `${quando.getFullYear()}-${due(quando.getMonth() + 1)}-${due(quando.getDate())}`
    + `T${due(quando.getHours())}:${due(quando.getMinutes())}`;
};

const daInput = (locale) => {
  if (!locale) return null;
  const quando = new Date(locale);
  return Number.isNaN(quando.getTime()) ? null : quando.toISOString();
};

/** Dalla risposta dell'API alla bozza che si modifica a schermo. */
const inBozza = (dati) => ({
  titolo: dati.titolo || '',
  sottotitolo: dati.sottotitolo || '',
  data: dati.data || '',
  ora: ora(dati.ora),
  luogo: dati.luogo || '',
  indirizzo_arrivo: dati.indirizzo_arrivo || '',
  descrizione: dati.descrizione || '',
  info_utili: dati.info_utili || '',
  biglietti_nominativi: !!dati.biglietti_nominativi,
  vendita_apre_il: perInput(dati.vendita_apre_il),
  vendita_chiude_il: perInput(dati.vendita_chiude_il),
  note_interne: dati.note_interne || '',
  navette: (dati.navette || []).map((n) => ({
    _k: chiave(),
    id: n.id,
    nome: n.nome || '',
    mezzo: n.mezzo || '',
    posti_totali: n.posti_totali ?? '',
    prezzo: n.prezzo ?? '',
    ora_arrivo: ora(n.ora_arrivo),
    ora_rientro: ora(n.ora_rientro),
    attiva: n.attiva !== false,
    venduti: n.posti_venduti || 0,
    impegnati: n.posti_impegnati || 0,
    fermate: (n.fermate || []).map((f) => ({
      _k: chiave(), id: f.id, luogo: f.luogo || '',
      indirizzo: f.indirizzo || '', ora: ora(f.ora),
    })),
  })),
});

/** Una fermata del tutto vuota non e' un dato: non si manda. */
const fermataCompilata = (f) => !!(f.luogo.trim() || f.ora || (f.indirizzo || '').trim());

/** Quel che si manda al server. Serve anche a capire se c'e' da salvare. */
const inRichiesta = (b) => ({
  titolo: b.titolo.trim(),
  sottotitolo: b.sottotitolo.trim(),
  data: b.data,
  ora: b.ora || null,
  luogo: b.luogo.trim(),
  indirizzo_arrivo: b.indirizzo_arrivo.trim(),
  descrizione: b.descrizione,
  info_utili: b.info_utili,
  biglietti_nominativi: b.biglietti_nominativi,
  vendita_apre_il: daInput(b.vendita_apre_il),
  vendita_chiude_il: daInput(b.vendita_chiude_il),
  note_interne: b.note_interne,
  navette: b.navette.map((n) => ({
    ...(n.id ? { id: n.id } : {}),
    nome: n.nome.trim(),
    mezzo: n.mezzo === '' || n.mezzo === null ? null : Number(n.mezzo),
    ...(String(n.posti_totali) === '' ? {} : { posti_totali: Number(n.posti_totali) }),
    prezzo: String(n.prezzo === '' ? '0.00' : n.prezzo),
    ora_arrivo: n.ora_arrivo || null,
    ora_rientro: n.ora_rientro || null,
    attiva: !!n.attiva,
    fermate: n.fermate.filter(fermataCompilata).map((f) => ({
      ...(f.id ? { id: f.id } : {}),
      luogo: f.luogo.trim(),
      indirizzo: (f.indirizzo || '').trim(),
      ora: f.ora || null,
    })),
  })),
});

/**
 * Il primo messaggio leggibile dentro la risposta d'errore, a qualunque
 * profondita' stia: con le partenze annidate l'errore arriva come
 * elenco di oggetti, e un "[object Object]" a video non aiuta nessuno.
 */
const primoErrore = (errore) => {
  const cerca = (valore) => {
    if (!valore) return null;
    if (typeof valore === 'string') return valore;
    if (Array.isArray(valore)) {
      for (const voce of valore) {
        const trovato = cerca(voce);
        if (trovato) return trovato;
      }
      return null;
    }
    if (typeof valore === 'object') {
      if (valore.errore) return cerca(valore.errore);
      for (const nome of Object.keys(valore)) {
        const trovato = cerca(valore[nome]);
        if (trovato) return trovato;
      }
    }
    return null;
  };
  return cerca(errore.response?.data);
};

/** Quel che manca per poter salvare, oppure null. */
const cosaManca = (b) => {
  if (!b.titolo.trim()) return 'Il titolo serve.';
  if (!b.data) return 'La data dell\'evento serve.';
  for (let i = 0; i < b.navette.length; i += 1) {
    const n = b.navette[i];
    const quale = n.nome.trim() || `la partenza ${i + 1}`;
    if (String(n.posti_totali) === '' && !n.mezzo) {
      return `Per ${quale} indica quanti posti vendere, oppure scegli un mezzo.`;
    }
    const sbagliata = n.fermate.findIndex((f) => f.ora && !f.luogo.trim());
    if (sbagliata >= 0) {
      return `La fermata ${sbagliata + 1} di ${quale} ha l'ora ma non il luogo.`;
    }
  }
  return null;
};

const EventoDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const nuovo = id === 'nuovo';

  const [bozza, setBozza] = useState(EVENTO_VUOTO);
  const [salvato, setSalvato] = useState(null); // la richiesta dell'ultimo stato salvato
  const [stato, setStato] = useState('BOZZA');
  const [mezzi, setMezzi] = useState([]);
  const [caricamento, setCaricamento] = useState(!nuovo);
  const [salvataggio, setSalvataggio] = useState(false);
  const [altre, setAltre] = useState(false);
  /* Chi sale dove: arriva da una richiesta a parte, perche' e' un
     conto sugli ordini incassati e non un campo dell'evento. */
  const [salite, setSalite] = useState(null);

  /* La foto viaggia a parte: un file non e' un campo di un modulo
     JSON. Resta qui in attesa e parte col salvataggio, cosi' per chi
     la usa e' un gesto solo. */
  const [foto, setFoto] = useState({ url: null, file: null, anteprima: null, togli: false });

  /* Il confronto e' su quel che si manderebbe: due bozze che
     produrrebbero la stessa richiesta non sono da salvare, anche se a
     schermo qualcosa e' stato scritto e poi cancellato. */
  const richiesta = useMemo(() => JSON.stringify(inRichiesta(bozza)), [bozza]);
  const sporco = (salvato !== null && richiesta !== salvato)
    || !!foto.file || foto.togli;

  const ricorda = useCallback((b) => setSalvato(JSON.stringify(inRichiesta(b))), []);

  const carica = useCallback(async () => {
    if (nuovo) return;
    setCaricamento(true);
    try {
      const { data } = await api.get(`/navette/eventi/${id}/`);
      const fresca = inBozza(data);
      setBozza(fresca);
      ricorda(fresca);
      setStato(data.stato);
      setFoto({ url: data.immagine || null, file: null, anteprima: null, togli: false });
    } catch (errore) {
      console.error('Errore caricamento evento:', errore);
      notify.error('Evento non trovato');
      navigate('/admin/eventi');
    } finally {
      setCaricamento(false);
    }
  }, [id, nuovo, navigate, ricorda]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    // Per un evento nuovo il riferimento e' la bozza vuota di
    // partenza: finche' non si scrive niente non c'e' niente da
    // salvare, e uscire non chiede conferma per nulla.
    if (nuovo && salvato === null) ricorda(bozza);
  }, [nuovo, salvato, bozza, ricorda]);

  useEffect(() => {
    getAll('/trasporti/mezzi/', { attivo: true }).then(setMezzi).catch(() => setMezzi([]));
  }, []);

  /* L'elenco di chi sale si chiede solo quando c'e' qualcuno che sale:
     su un evento appena aperto sarebbe una richiesta per niente. */
  const vendutiTotali = bozza.navette.reduce((s, n) => s + (n.venduti || 0), 0);
  useEffect(() => {
    if (nuovo || vendutiTotali === 0) { setSalite(null); return undefined; }
    let vivo = true;
    api.get(`/navette/eventi/${id}/passeggeri/`)
      .then(({ data }) => { if (vivo) setSalite(data); })
      .catch(() => { if (vivo) setSalite(null); });
    return () => { vivo = false; };
  }, [id, nuovo, vendutiTotali]);

  /* Uscire con modifiche aperte. Due strade diverse: chiudere la
     pagina lo chiede il browser, cambiare schermata lo chiede il menu
     - che naviga chiamando `navigate` e non con un collegamento, per
     cui un controllo sui click non lo vedrebbe passare. */
  useEffect(() => {
    if (!sporco) return undefined;

    const avvisaIlBrowser = (e) => { e.preventDefault(); e.returnValue = ''; };
    const chiedi = () => window.confirm(ABBANDONA);

    window.addEventListener('beforeunload', avvisaIlBrowser);
    chiediPrimaDiUscire(chiedi);
    return () => {
      window.removeEventListener('beforeunload', avvisaIlBrowser);
      smettiDiChiedere(chiedi);
    };
  }, [sporco]);

  const esci = () => {
    if (sporco && !window.confirm(ABBANDONA)) return;
    navigate('/admin/eventi');
  };

  const campo = (nome, valore) => setBozza((b) => ({ ...b, [nome]: valore }));

  /* ---- foto ---- */
  const scegliFoto = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return notify.error('Serve una foto: JPEG, PNG o WEBP.');
    }
    if (file.size > 12 * 1024 * 1024) {
      return notify.error(`La foto pesa ${Math.round(file.size / 1024 / 1024)} MB: il massimo e 12 MB.`);
    }
    setFoto((f) => {
      if (f.anteprima) URL.revokeObjectURL(f.anteprima);
      return { ...f, file, anteprima: URL.createObjectURL(file), togli: false };
    });
  };

  const togliFoto = () => setFoto((f) => {
    if (f.anteprima) URL.revokeObjectURL(f.anteprima);
    // `togli` serve solo se una foto c'era davvero sul server: altrimenti
    // basta buttare via il file scelto e non salvato.
    return { url: f.url, file: null, anteprima: null, togli: !!f.url };
  });

  // Gli indirizzi temporanei dell'anteprima vanno liberati, o la
  // memoria del browser cresce a ogni foto provata.
  useEffect(() => () => { if (foto.anteprima) URL.revokeObjectURL(foto.anteprima); },
    [foto.anteprima]);

  /* ---- partenze, solo in memoria ---- */
  const cambiaNavetta = (indice, modifiche) => setBozza((b) => ({
    ...b,
    navette: b.navette.map((n, i) => (i === indice ? { ...n, ...modifiche } : n)),
  }));

  const aggiungiNavetta = () => setBozza((b) => ({
    ...b, navette: [...b.navette, NAVETTA_VUOTA()],
  }));

  const togliNavetta = (indice) => {
    const n = bozza.navette[indice];
    const presi = n.venduti + n.impegnati;
    if (presi > 0) {
      return notify.error(
        `Su "${n.nome || `partenza ${indice + 1}`}" ci sono ${presi} posti presi: `
        + 'non si puo togliere. Togli la spunta "Attiva" per non venderla piu.'
      );
    }
    if (n.id && !window.confirm(`Togliere "${n.nome || `partenza ${indice + 1}`}" dall'evento?`)) return;
    setBozza((b) => ({ ...b, navette: b.navette.filter((_, i) => i !== indice) }));
  };

  const spostaNavetta = (indice, verso) => setBozza((b) => {
    const altra = indice + verso;
    if (altra < 0 || altra >= b.navette.length) return b;
    const navette = [...b.navette];
    [navette[indice], navette[altra]] = [navette[altra], navette[indice]];
    return { ...b, navette };
  });

  /* ---- fermate, solo in memoria ---- */
  const cambiaFermata = (iNavetta, iFermata, modifiche) => setBozza((b) => ({
    ...b,
    navette: b.navette.map((n, i) => (i !== iNavetta ? n : {
      ...n,
      fermate: n.fermate.map((f, j) => (j === iFermata ? { ...f, ...modifiche } : f)),
    })),
  }));

  const aggiungiFermata = (iNavetta) => setBozza((b) => ({
    ...b,
    navette: b.navette.map((n, i) => (i !== iNavetta ? n : {
      ...n, fermate: [...n.fermate, { _k: chiave(), luogo: '', ora: '' }],
    })),
  }));

  const togliFermata = (iNavetta, iFermata) => setBozza((b) => ({
    ...b,
    navette: b.navette.map((n, i) => (i !== iNavetta ? n : {
      ...n, fermate: n.fermate.filter((_, j) => j !== iFermata),
    })),
  }));

  const spostaFermata = (iNavetta, iFermata, verso) => setBozza((b) => ({
    ...b,
    navette: b.navette.map((n, i) => {
      if (i !== iNavetta) return n;
      const altra = iFermata + verso;
      if (altra < 0 || altra >= n.fermate.length) return n;
      const fermate = [...n.fermate];
      [fermate[iFermata], fermate[altra]] = [fermate[altra], fermate[iFermata]];
      return { ...n, fermate };
    }),
  }));

  const mandaFoto = async (idEvento) => {
    if (foto.togli && !foto.file) {
      const via = await api.delete(`/navette/eventi/${idEvento}/immagine/`);
      return via.data;
    }
    const modulo = new FormData();
    modulo.append('immagine', foto.file);
    const caricata = await api.post(`/navette/eventi/${idEvento}/immagine/`, modulo, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return caricata.data;
  };

  /* ---- salvataggio: i dati in una richiesta, la foto subito dopo ---- */
  const salva = async () => {
    const manca = cosaManca(bozza);
    if (manca) return notify.error(manca);

    setSalvataggio(true);
    try {
      const corpo = inRichiesta(bozza);
      const risposta = nuovo
        ? await api.post('/navette/eventi/', corpo)
        : await api.patch(`/navette/eventi/${id}/`, corpo);

      let dati = risposta.data;

      /* La foto parte dopo, quando l'evento ha un id. Se non passa,
         l'evento resta salvato lo stesso e lo si dice: perdere i dati
         scritti perche' una foto e' troppo pesante sarebbe peggio. */
      if (foto.file || foto.togli) {
        try {
          dati = await mandaFoto(dati.id);
        } catch (errore) {
          notify.warning(primoErrore(errore) || 'Evento salvato, ma la foto non e stata caricata.');
        }
      }

      const fresca = inBozza(dati);
      setBozza(fresca);
      ricorda(fresca);
      setStato(dati.stato);
      setFoto({ url: dati.immagine || null, file: null, anteprima: null, togli: false });
      notify.success(nuovo ? 'Evento creato' : 'Evento salvato');
      if (nuovo) navigate(`/admin/eventi/${dati.id}`, { replace: true });
    } catch (errore) {
      notify.error(primoErrore(errore) || 'Errore nel salvataggio');
    } finally {
      setSalvataggio(false);
    }
  };

  const cambiaStato = async (nuovoStato) => {
    const avviso = notify.loading('Aggiorno…');
    try {
      const risposta = await api.post(`/navette/eventi/${id}/cambia_stato/`, { stato: nuovoStato });
      setStato(risposta.data.stato);
      notify.update(avviso, {
        type: 'success',
        render: nuovoStato === 'PUBBLICATO' ? 'Evento in vendita sul sito' : 'Stato aggiornato',
      });
    } catch (errore) {
      notify.update(avviso, {
        type: 'error',
        render: primoErrore(errore) || 'Non riesco a cambiare stato',
      });
    }
  };

  const attive = bozza.navette.filter((n) => n.attiva);

  const pubblica = () => {
    if (sporco) return notify.error('Salva le modifiche prima di pubblicare.');
    if (attive.length === 0) {
      return notify.error('Senza partenze attive non c\'è niente da vendere.');
    }
    const senzaFermate = attive.filter((n) => !n.fermate.some(fermataCompilata));
    if (senzaFermate.length) {
      return notify.error(
        'Senza fermate il sito non sa da dove si parte: aggiungine almeno una a '
        + senzaFermate.map((n, i) => `"${n.nome || `partenza ${i + 1}`}"`).join(', ') + '.'
      );
    }
    const gratis = attive.filter((n) => Number(n.prezzo || 0) === 0);
    if (gratis.length && !window.confirm(
      `${gratis.length === 1 ? 'Una partenza è' : `${gratis.length} partenze sono`} a 0 €: `
      + 'i clienti non pagheranno niente. Pubblico comunque?'
    )) return;
    cambiaStato('PUBBLICATO');
  };

  /* Il foglio da portare a bordo. Lo prepara il server: la stessa
     funzione che serve l'elenco a schermo, cosi' il foglio stampato e
     lo schermo non possono dire cose diverse. */
  const scaricaPdfPasseggeri = async () => {
    const avviso = notify.loading('Preparo il foglio…');
    try {
      const risposta = await api.get(`/navette/eventi/${id}/passeggeri-pdf/`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([risposta.data], { type: 'application/pdf' }));
      // Da telefono `window.open` dopo una chiamata asincrona viene
      // bloccato come popup: il salvataggio va avviato da un <a>, che
      // il browser non considera una finestra nuova.
      const link = document.createElement('a');
      link.href = url;
      link.download = `passeggeri-${(bozza.titolo || 'evento').replace(/\W+/g, '-').toLowerCase()}-${bozza.data}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      notify.update(avviso, { type: 'success', render: 'Elenco scaricato' });
    } catch (errore) {
      notify.update(avviso, {
        type: 'error',
        render: 'Non riesco a preparare il foglio',
      });
    }
  };

  const scaricaPasseggeri = async () => {
    try {
      const { data } = await api.get(`/navette/eventi/${id}/passeggeri/`);
      const righe = [[
        'Partenza', 'Fermata', 'Ora fermata', 'Biglietto', 'Nome',
        'Telefono', 'Ordine', 'Salito',
      ]];
      // In ordine di fermata, non di acquisto: e' l'ordine in cui
      // l'autista incontra la gente lungo la strada.
      data.navette.forEach((n) => {
        (n.per_fermata || []).forEach((f) => {
          f.passeggeri.forEach((p) => {
            righe.push([
              n.etichetta, f.luogo, ora(f.ora), p.biglietto, p.nome,
              p.telefono, p.ordine, p.salito ? 'sì' : 'no',
            ]);
          });
        });
      });
      if (righe.length === 1) return notify.info('Nessun passeggero confermato.');

      // Punto e virgola e BOM: Excel in italiano apre il file senza
      // chiedere nulla e con gli accenti giusti.
      const csv = '﻿' + righe
        .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
        .join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `passeggeri-${(data.evento || 'evento').replace(/\W+/g, '-').toLowerCase()}-${data.data}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (errore) {
      notify.error('Non riesco a scaricare l\'elenco');
    }
  };

  if (caricamento) {
    return <AdminLayout><div className="p-6 text-gray-400">Carico…</div></AdminLayout>;
  }

  const etichettaStato = STATI_EVENTO[stato] || STATI_EVENTO.BOZZA;
  const venduti = attive.reduce((s, n) => s + n.venduti, 0);
  const impegnati = attive.reduce((s, n) => s + n.impegnati, 0);
  const postiTotali = attive.reduce((s, n) => s + (Number(n.posti_totali) || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl pb-28">
        <button
          onClick={esci}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Eventi
        </button>

        <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">
                {nuovo ? 'Nuovo evento' : (bozza.titolo || 'Senza titolo')}
              </h1>
              {!nuovo && (
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${etichettaStato.classe}`}>
                  {etichettaStato.label}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {nuovo
                ? 'Compila i dati e le partenze, poi salva: finché non salvi non c\'è niente sul sito.'
                : `${venduti} venduti · ${impegnati} impegnati su ${postiTotali} posti`}
            </p>
          </div>

          {!nuovo && venduti > 0 && (
            <div className="flex items-center gap-1">
              <button
                onClick={scaricaPdfPasseggeri}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
              >
                <Printer className="w-4 h-4" />
                Elenco passeggeri
              </button>
              {/* Il CSV resta per chi lo rimaneggia in Excel. Il foglio
                  che va a bordo e' il PDF, percio' e' quello il
                  pulsante con il bordo. */}
              <button
                onClick={scaricaPasseggeri}
                title="Lo stesso elenco come foglio di calcolo"
                className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
            </div>
          )}
        </div>

        {/* L'evento */}
        <Riquadro titolo="L'evento">
          <div className="grid sm:grid-cols-6 gap-4">
            <Campo etichetta="Titolo" obbligatorio className="sm:col-span-6">
              <input
                type="text" value={bozza.titolo} disabled={!modificabile}
                onChange={(e) => campo('titolo', e.target.value)}
                placeholder="Concerto allo Stadio Maradona"
                className={classeInput}
              />
            </Campo>
            <Campo etichetta="Data" obbligatorio className="sm:col-span-2">
              <input
                type="date" value={bozza.data} disabled={!modificabile}
                onChange={(e) => campo('data', e.target.value)}
                className={classeInput}
              />
            </Campo>
            <Campo etichetta="Ora dell'evento" className="sm:col-span-2"
                   aiuto="Quando inizia il concerto, non quando parte il pullman.">
              <input
                type="time" value={bozza.ora} disabled={!modificabile}
                onChange={(e) => campo('ora', e.target.value)}
                className={classeInput}
              />
            </Campo>
            <Campo etichetta="Luogo" className="sm:col-span-2">
              <input
                type="text" value={bozza.luogo} disabled={!modificabile}
                onChange={(e) => campo('luogo', e.target.value)}
                placeholder="Stadio Maradona, Napoli"
                className={classeInput}
              />
            </Campo>
            <Campo etichetta="Indirizzo di arrivo" className="sm:col-span-6"
                   aiuto="Dove scende il cliente. Il sito ci costruisce sopra il collegamento alla mappa.">
              <input
                type="text" value={bozza.indirizzo_arrivo} disabled={!modificabile}
                onChange={(e) => campo('indirizzo_arrivo', e.target.value)}
                placeholder="Piazza Walther 1, 39100 Bolzano BZ"
                className={classeInput}
              />
            </Campo>
            <Campo etichetta="Sottotitolo" className="sm:col-span-6"
                   aiuto="Compare sotto il titolo nel riquadro del sito.">
              <input
                type="text" value={bozza.sottotitolo} disabled={!modificabile}
                onChange={(e) => campo('sottotitolo', e.target.value)}
                placeholder="Navetta andata e ritorno"
                className={classeInput}
              />
            </Campo>
          </div>

          <button
            onClick={() => setAltre(!altre)}
            className="flex items-center gap-1.5 mt-4 text-sm text-gray-600 hover:text-gray-900 transition"
          >
            {altre ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Finestra di vendita, biglietti nominativi e note
          </button>

          {altre && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-4">
              <Campo etichetta="Vendite aperte dal" aiuto="Vuoto: da subito.">
                <input
                  type="datetime-local" value={bozza.vendita_apre_il} disabled={!modificabile}
                  onChange={(e) => campo('vendita_apre_il', e.target.value)}
                  className={classeInput}
                />
              </Campo>
              <Campo etichetta="Vendite chiuse dal"
                     aiuto="Vuoto: si vende fino al giorno dell'evento.">
                <input
                  type="datetime-local" value={bozza.vendita_chiude_il} disabled={!modificabile}
                  onChange={(e) => campo('vendita_chiude_il', e.target.value)}
                  className={classeInput}
                />
              </Campo>
              <Campo etichetta="Note interne" className="sm:col-span-2"
                     aiuto="Non compaiono sul sito.">
                <textarea
                  rows={2} value={bozza.note_interne} disabled={!modificabile}
                  onChange={(e) => campo('note_interne', e.target.value)}
                  className={classeInput}
                />
              </Campo>
              <label className="sm:col-span-2 flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox" checked={bozza.biglietti_nominativi} disabled={!modificabile}
                  onChange={(e) => campo('biglietti_nominativi', e.target.checked)}
                  className="w-4 h-4 mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900">Biglietti nominativi</span>
                  <span className="block text-gray-500 mt-0.5">
                    Chiede nome e cognome di ogni passeggero, non solo di chi compra.
                    Da attivare quando serve sapere chi sale: su sei posti comprati
                    da un gruppo di amici, sei nomi da digitare fanno abbandonare l'acquisto.
                  </span>
                </span>
              </label>
            </div>
          )}
        </Riquadro>

        {/* Quello che il cliente legge prima di comprare */}
        <Riquadro
          titolo="La pagina sul sito"
          sottotitolo="Quello che il cliente vede prima di decidere. Una foto e due righe scritte bene valgono più di uno sconto."
        >
          <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-5">
            <RiquadroFoto
              foto={foto}
              modificabile={modificabile}
              onScegli={scegliFoto}
              onTogli={togliFoto}
            />

            <div className="space-y-4">
              <Campo etichetta="Descrizione"
                     aiuto="Che cos'è e com'è la giornata. Due o tre frasi.">
                <textarea
                  rows={4} value={bozza.descrizione} disabled={!modificabile}
                  onChange={(e) => campo('descrizione', e.target.value)}
                  placeholder="Partenza la mattina presto, rientro in serata. Il posto è assegnato solo sulla navetta: ai mercatini si gira liberi."
                  className={classeInput}
                />
              </Campo>
              <Campo etichetta="Cose da sapere"
                     aiuto="Una riga per punto: il sito le mette in elenco.">
                <textarea
                  rows={4} value={bozza.info_utili} disabled={!modificabile}
                  onChange={(e) => campo('info_utili', e.target.value)}
                  placeholder={'Portare un documento\nVestirsi pesante: a Bolzano ci sono 2 gradi\nIl viaggio dura circa 4 ore'}
                  className={classeInput}
                />
              </Campo>
            </div>
          </div>

          {!nuovo && stato === 'PUBBLICATO' && (
            <a
              href={`https://www.casellanoleggiobus.it/eventi/${id}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-600 hover:text-gray-900 transition"
            >
              <ExternalLink className="w-4 h-4" />
              Vedi la pagina come la vede il cliente
            </a>
          )}
        </Riquadro>

        {/* Partenze */}
        <Riquadro
          titolo="Partenze"
          sottotitolo="Una per ogni corsa, con le sue fermate. Il prezzo sta sulla partenza: da Caserta o da Vitulazio non è la stessa distanza."
        >
          {bozza.navette.length === 0 ? (
            <div className="py-10 text-center">
              <Bus className="w-9 h-9 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Nessuna partenza: non c'è niente da vendere.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bozza.navette.map((navetta, indice) => (
                <SchedaNavetta
                  key={navetta._k}
                  navetta={navetta}
                  indice={indice}
                  ultima={indice === bozza.navette.length - 1}
                  mezzi={mezzi}
                  modificabile={modificabile}
                  onCambia={(modifiche) => cambiaNavetta(indice, modifiche)}
                  onTogli={() => togliNavetta(indice)}
                  onSposta={(verso) => spostaNavetta(indice, verso)}
                  onCambiaFermata={(iF, modifiche) => cambiaFermata(indice, iF, modifiche)}
                  onAggiungiFermata={() => aggiungiFermata(indice)}
                  onTogliFermata={(iF) => togliFermata(indice, iF)}
                  onSpostaFermata={(iF, verso) => spostaFermata(indice, iF, verso)}
                />
              ))}
            </div>
          )}

          {modificabile && (
            <button
              onClick={aggiungiNavetta}
              className="flex items-center gap-1.5 mt-4 px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:border-gray-900 hover:text-gray-900 text-gray-600 transition text-sm font-medium w-full justify-center"
            >
              <Plus className="w-4 h-4" />
              Aggiungi un'altra partenza
            </button>
          )}
        </Riquadro>

        {/* Chi sale dove */}
        {salite && (
          <Riquadro
            titolo="Chi sale dove"
            sottotitolo="Gli ordini incassati, fermata per fermata. È la lista che serve all'autista: dove fermarsi, chi aspettare, e dove tirare dritto."
          >
            <div className="space-y-5">
              {salite.navette.map((n) => (
                <div key={n.navetta}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <span className="font-semibold text-gray-900">{n.etichetta}</span>
                    <span className="text-sm text-gray-500 shrink-0">
                      {n.posti_venduti} su {n.posti_totali} posti
                    </span>
                  </div>

                  {(n.per_fermata || []).length === 0 ? (
                    <p className="text-sm text-gray-500">
                      Questa partenza non ha fermate: nessuno sa dove presentarsi.
                    </p>
                  ) : (
                    <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {n.per_fermata.map((f) => {
                        const quanti = f.passeggeri.length;
                        return (
                          <li key={f.fermata ?? 'ignota'}
                              className={`p-3 ${quanti ? '' : 'bg-gray-50'}`}>
                            <div className="flex items-baseline justify-between gap-3">
                              <span className={`text-sm font-medium ${quanti ? 'text-gray-900' : 'text-gray-400'}`}>
                                {f.luogo}{f.ora ? ` · ${ora(f.ora)}` : ''}
                              </span>
                              <span className={`text-xs font-semibold shrink-0 ${quanti ? 'text-gray-700' : 'text-gray-400'}`}>
                                {quanti === 0
                                  ? 'nessuno: non fermarsi'
                                  : `${quanti} ${quanti === 1 ? 'persona' : 'persone'}`}
                              </span>
                            </div>
                            {quanti > 0 && (
                              <p className="text-sm text-gray-500 mt-1">
                                {f.passeggeri.map((p) => p.nome).join(' · ')}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Riquadro>
        )}

        {/* Vendita sul sito: solo quando l'evento esiste davvero */}
        {!nuovo && modificabile && (
          <Riquadro titolo="Vendita sul sito">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500 min-w-0">
                {stato === 'PUBBLICATO'
                  ? 'L\'evento è in vetrina e i clienti possono comprare.'
                  : 'L\'evento non è visibile sul sito.'}
              </p>
              <div className="flex gap-2 flex-wrap">
                {stato !== 'PUBBLICATO' && (
                  <button
                    onClick={pubblica}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    Pubblica
                  </button>
                )}
                {stato === 'PUBBLICATO' && (
                  <button
                    onClick={() => cambiaStato('CHIUSO')}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
                  >
                    <Lock className="w-4 h-4" />
                    Chiudi le vendite
                  </button>
                )}
                {stato !== 'ANNULLATO' && (
                  <button
                    onClick={() => {
                      if (window.confirm('Annullare l\'evento? Gli ordini già confermati restano: vanno rimborsati a mano.')) {
                        cambiaStato('ANNULLATO');
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:border-red-600 hover:text-red-600 transition text-sm font-medium"
                  >
                    Annulla evento
                  </button>
                )}
              </div>
            </div>
            {sporco && stato !== 'PUBBLICATO' && (
              <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                Ci sono modifiche non salvate: salva prima di pubblicare, o il sito
                mostrerebbe la versione di prima.
              </div>
            )}
          </Riquadro>
        )}
      </div>

      {/* Barra del salvataggio: l'unico posto da cui si scrive */}
      {modificabile && (
        <div className="sticky bottom-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-500 min-w-0 truncate">
            {sporco ? (
              <span className="flex items-center gap-2 text-amber-700 font-medium">
                <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                Modifiche non salvate
              </span>
            ) : nuovo ? 'Niente è ancora stato salvato.' : 'Tutto salvato.'}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={esci}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition"
            >
              Annulla
            </button>
            <button
              onClick={salva}
              disabled={salvataggio || (!sporco && !nuovo)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm disabled:opacity-40"
            >
              <Save className="w-4 h-4" />
              {salvataggio ? 'Salvo…' : nuovo ? 'Salva bozza' : 'Salva'}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

/* ------------------------------------------------------------------ */

const classeInput =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-900 disabled:bg-gray-50 disabled:text-gray-500';

const Riquadro = ({ titolo, sottotitolo, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
    <h2 className="font-semibold text-gray-900">{titolo}</h2>
    {sottotitolo && <p className="text-sm text-gray-500 mt-0.5 mb-4">{sottotitolo}</p>}
    <div className={sottotitolo ? '' : 'mt-4'}>{children}</div>
  </div>
);

/**
 * Il riquadro della foto: anteprima, scelta, rimozione.
 *
 * L'anteprima e' quella del file scelto finche' non si salva, cosi'
 * si vede subito se la foto e' quella giusta invece di scoprirlo dopo
 * il caricamento.
 *
 * La foto si cambia cliccando la foto. Prima, su un evento che ne
 * aveva gia' una, l'unico modo era il "Cambia" scritto piccolo sotto
 * il riquadro: in ufficio non si trovava, e sembrava che la foto non
 * si potesse piu' toccare. E si apre con una `label` legata al campo
 * file, non con un click simulato su un campo nascosto: quello
 * dipende da come il browser tratta un elemento che non si vede,
 * questo e' il modo normale in cui funziona un modulo.
 */
const RiquadroFoto = ({ foto, modificabile, onScegli, onTogli }) => {
  const idCampo = useId();
  const [sopra, setSopra] = useState(false);
  const mostrata = foto.anteprima || (foto.togli ? null : foto.url);

  const lascia = (e) => {
    e.preventDefault();
    setSopra(false);
    if (!modificabile) return;
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) onScegli(file);
  };

  // In sola consultazione il riquadro non apre niente: una `label` che
  // non fa nulla al clic prometterebbe una modifica che non c'e'.
  const Riquadro = modificabile ? 'label' : 'div';

  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-1.5">Foto</div>

      <Riquadro
        htmlFor={modificabile ? idCampo : undefined}
        onDragOver={(e) => { e.preventDefault(); setSopra(true); }}
        onDragLeave={() => setSopra(false)}
        onDrop={lascia}
        className={`group relative block rounded-lg border overflow-hidden transition
          ${modificabile ? 'cursor-pointer' : ''}
          ${sopra ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-300'}
          ${mostrata ? '' : 'border-dashed'}`}
        style={{ aspectRatio: '4 / 3' }}
      >
        {mostrata ? (
          <>
            <img src={mostrata} alt="Foto dell'evento"
                 className="w-full h-full object-cover" />
            {modificabile && (
              /* La scritta si accende passandoci sopra: dice che la
                 foto si cambia cliccandola, senza coprirla sempre. */
              <span className="absolute inset-0 flex items-center justify-center gap-2
                               text-sm font-medium text-white opacity-0 transition
                               group-hover:opacity-100 group-hover:bg-black/45">
                <ImagePlus className="w-5 h-5" />
                Cambia foto
              </span>
            )}
          </>
        ) : (
          <span className={`w-full h-full flex flex-col items-center justify-center gap-2
            text-gray-400 transition
            ${modificabile ? 'group-hover:text-gray-700 group-hover:bg-gray-50' : ''}`}>
            <ImagePlus className="w-8 h-8" />
            <span className="text-sm font-medium">
              {modificabile ? 'Scegli una foto' : 'Nessuna foto'}
            </span>
            {modificabile && <span className="text-xs">oppure trascinala qui</span>}
          </span>
        )}
      </Riquadro>

      {/* Nascosto alla vista ma non al browser: `display: none` lo
          toglie dalla pagina, e c'e' chi allora non apre piu' la
          finestra di scelta. */}
      <input
        id={idCampo}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        aria-label="Foto dell'evento"
        className="sr-only"
        disabled={!modificabile}
        onChange={(e) => { onScegli(e.target.files?.[0]); e.target.value = ''; }}
      />

      {modificabile && (
        <div className="flex items-center gap-3 mt-2">
          <label
            htmlFor={idCampo}
            className="text-sm font-medium text-gray-700 hover:text-gray-900 transition cursor-pointer"
          >
            {mostrata ? 'Cambia' : 'Scegli'}
          </label>
          {mostrata && (
            <button
              type="button"
              onClick={onTogli}
              className="text-sm font-medium text-gray-500 hover:text-red-600 transition"
            >
              Togli
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-1.5">
        {foto.file
          ? 'Scelta: parte col salvataggio.'
          : foto.togli
            ? 'Verrà tolta al salvataggio.'
            : 'Viene rimpicciolita e alleggerita da sola. JPEG, PNG o WEBP fino a 12 MB.'}
      </p>
    </div>
  );
};

const Campo = ({ etichetta, obbligatorio, aiuto, className = '', children }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {etichetta}{obbligatorio && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {aiuto && <p className="text-xs text-gray-500 mt-1">{aiuto}</p>}
  </div>
);

const SchedaNavetta = ({
  navetta, indice, ultima, mezzi, modificabile,
  onCambia, onTogli, onSposta,
  onCambiaFermata, onAggiungiFermata, onTogliFermata, onSpostaFermata,
}) => {
  const mezzo = mezzi.find((m) => m.id === Number(navetta.mezzo));
  const posti = Number(navetta.posti_totali) || 0;
  const troppi = mezzo && posti > mezzo.capienza;
  const presi = navetta.venduti + navetta.impegnati;
  const sottoIVenduti = navetta.id && posti > 0 && posti < presi;
  const fermate = navetta.fermate;
  const partenza = fermate.find(fermataCompilata)?.ora || '';
  const nome = navetta.nome.trim()
    || fermate.find((f) => f.luogo.trim())?.luogo
    || `Partenza ${indice + 1}`;

  return (
    <div className={`rounded-lg border p-4 ${navetta.attiva ? 'border-gray-200' : 'border-dashed border-gray-300 bg-gray-50'}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-6 h-6 shrink-0 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center">
            {indice + 1}
          </span>
          <span className="font-semibold text-gray-900 truncate">{nome}</span>
          {!navetta.attiva && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-200 text-gray-600 shrink-0">
              Non in vendita
            </span>
          )}
        </div>
        {modificabile && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onSposta(-1)} disabled={indice === 0}
              className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-25 transition"
              aria-label="Sposta su"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onSposta(1)} disabled={ultima}
              className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-25 transition"
              aria-label="Sposta giù"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              onClick={onTogli}
              className="p-1.5 text-gray-300 hover:text-red-600 transition"
              aria-label={`Togli ${nome}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-6 gap-3 mb-3">
        <Campo etichetta="Nome" className="sm:col-span-6"
               aiuto="Vuoto: il sito usa la prima fermata.">
          <input
            type="text" value={navetta.nome} disabled={!modificabile}
            onChange={(e) => onCambia({ nome: e.target.value })}
            placeholder="Partenza da Capua"
            className={classeInput}
          />
        </Campo>
        <div className="sm:col-span-3">
          <label className="block text-xs text-gray-500 mb-1">Mezzo</label>
          <select
            value={navetta.mezzo} disabled={!modificabile}
            onChange={(e) => {
              const scelto = mezzi.find((m) => m.id === Number(e.target.value));
              // Scegliendo il mezzo i posti si compilano da soli: e' il
              // dato che l'ufficio sbaglia piu' spesso a mano.
              const modifiche = { mezzo: e.target.value };
              if (scelto && presi === 0) modifiche.posti_totali = scelto.capienza;
              onCambia(modifiche);
            }}
            className={classeInput}
          >
            <option value="">Nessuno</option>
            {mezzi.map((m) => (
              <option key={m.id} value={m.id}>{m.targa} · {m.capienza} posti</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-3">
          <label className="block text-xs text-gray-500 mb-1">Posti in vendita</label>
          <input
            type="number" min="1" value={navetta.posti_totali} disabled={!modificabile}
            onChange={(e) => onCambia({ posti_totali: e.target.value })}
            className={`${classeInput} ${troppi || sottoIVenduti ? 'border-red-400' : ''}`}
          />
          {troppi && <p className="text-xs text-red-600 mt-1">Il mezzo ne ha {mezzo.capienza}.</p>}
          {sottoIVenduti && (
            <p className="text-xs text-red-600 mt-1">Ce ne sono già {presi} presi.</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Prezzo</label>
          <input
            type="number" min="0" step="0.50" value={navetta.prezzo} disabled={!modificabile}
            onChange={(e) => onCambia({ prezzo: e.target.value })}
            placeholder="0.00"
            className={classeInput}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Arrivo</label>
          <input
            type="time" value={navetta.ora_arrivo} disabled={!modificabile}
            onChange={(e) => onCambia({ ora_arrivo: e.target.value })}
            className={classeInput}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Rientro</label>
          <input
            type="time" value={navetta.ora_rientro} disabled={!modificabile}
            onChange={(e) => onCambia({ ora_rientro: e.target.value })}
            className={classeInput}
          />
        </div>
      </div>

      {/* Fermate: la prima e' la partenza, ed e' l'ora che finisce sul biglietto */}
      <div className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            Fermate, in ordine di salita
          </span>
          {partenza && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Parte alle {partenza}
            </span>
          )}
        </div>

        {fermate.length === 0 ? (
          <p className="text-xs text-amber-700 mb-2">
            Senza fermate il sito non sa da dove si parte e il biglietto non dice
            a che ora presentarsi.
          </p>
        ) : (
          <div className="space-y-2">
            {fermate.map((fermata, iF) => (
              <div key={fermata._k} className="rounded border border-gray-200 p-2.5">
                <div className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-xs text-gray-400 text-right">{iF + 1}.</span>
                <input
                  type="text" value={fermata.luogo} disabled={!modificabile}
                  onChange={(e) => onCambiaFermata(iF, { luogo: e.target.value })}
                  placeholder="Capua, Piazza dei Giudici"
                  className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-900 disabled:bg-gray-50"
                />
                <input
                  type="time" value={fermata.ora} disabled={!modificabile}
                  onChange={(e) => onCambiaFermata(iF, { ora: e.target.value })}
                  className="w-32 shrink-0 px-2.5 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:border-gray-900 disabled:bg-gray-50"
                />
                {modificabile && (
                  <>
                    <button
                      onClick={() => onSpostaFermata(iF, -1)} disabled={iF === 0}
                      className="p-1 text-gray-300 hover:text-gray-900 disabled:opacity-25 transition shrink-0"
                      aria-label="Sposta la fermata su"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onSpostaFermata(iF, 1)} disabled={iF === fermate.length - 1}
                      className="p-1 text-gray-300 hover:text-gray-900 disabled:opacity-25 transition shrink-0"
                      aria-label="Sposta la fermata giù"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onTogliFermata(iF)}
                      className="p-1 text-gray-300 hover:text-red-600 transition shrink-0"
                      aria-label="Togli la fermata"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                </div>
                {/* L'indirizzo esatto: serve a chi non e' del posto, e
                    il sito ci costruisce sopra il link alla mappa. */}
                <input
                  type="text" value={fermata.indirizzo || ''} disabled={!modificabile}
                  onChange={(e) => onCambiaFermata(iF, { indirizzo: e.target.value })}
                  placeholder="Indirizzo esatto per la mappa — Piazza dei Giudici 1, 81043 Capua CE"
                  className="w-full mt-2 ml-0 sm:ml-7 sm:w-[calc(100%-1.75rem)] px-2.5 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-gray-900 disabled:bg-gray-50"
                />
              </div>
            ))}
          </div>
        )}

        {modificabile && (
          <button
            onClick={onAggiungiFermata}
            className="mt-2 text-xs text-gray-500 hover:text-gray-900 transition"
          >
            + Aggiungi fermata
          </button>
        )}
      </div>

      {/* I contatori: solo per una partenza che esiste gia' nel database */}
      {navetta.id && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs mt-3 pt-3 border-t border-gray-100">
          <span className="flex items-center gap-1.5 text-gray-600">
            <Ticket className="w-3.5 h-3.5" />
            <strong className="text-gray-900">{navetta.venduti}</strong> venduti
          </span>
          <span className="flex items-center gap-1.5 text-gray-600">
            <Users className="w-3.5 h-3.5" />
            <strong className="text-gray-900">{navetta.impegnati}</strong> impegnati
          </span>
          <span className={posti - presi <= 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}>
            <strong>{Math.max(0, posti - presi)}</strong> liberi
          </span>
          <span className="text-gray-400">· {euro(navetta.prezzo)} a persona</span>
        </div>
      )}

      {modificabile && (
        <label className="flex items-center gap-2 mt-3 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox" checked={navetta.attiva}
            onChange={(e) => onCambia({ attiva: e.target.checked })}
            className="w-3.5 h-3.5"
          />
          In vendita sul sito
        </label>
      )}
    </div>
  );
};

export default EventoDettaglio;
